import React from "react";
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { selectChartAlertSettings } from "../../selectors/chart";
import { updateChartConfig } from "../../reducer/chart.reducer";
import { AlertSettings as AlertSettingsType } from "../chartV2/chart.typings";
import ColorSelector from "../colorSelector/colorSelector";
import { defaultAlertSettings } from "../../util/chart";
import {
    chartSettingsFormGroupSx,
    chartSettingsColorRowSx,
    chartSettingsSectionTitleSx,
    chartSettingsCheckboxColorLabelSx,
    chartSettingsCheckboxLabelSx,
} from "./chartSettingsLayout";

const checkboxLabelSx = chartSettingsCheckboxLabelSx;
const checkboxColorLabelSx = chartSettingsCheckboxColorLabelSx;

type Props = {
    viewKey: string;
};

const AlertSettings = ({ viewKey }: Props) => {
    const dispatch = useDispatch();
    const alertSettings = useSelector(selectChartAlertSettings(viewKey));

    const showAlertLines = alertSettings?.showAlertLines ?? defaultAlertSettings.showAlertLines;
    const alertLineColor = alertSettings?.alertLineColor ?? defaultAlertSettings.alertLineColor;
    const alertLineColorOpacity = alertSettings?.alertLineColorOpacity ?? defaultAlertSettings.alertLineColorOpacity;
    const onlyActiveAlerts = alertSettings?.onlyActiveAlerts ?? defaultAlertSettings.onlyActiveAlerts;

    const updateSettings = (partial: Partial<AlertSettingsType>) => {
        dispatch(updateChartConfig({
            viewKey,
            config: {
                alertSettings: {
                    ...alertSettings,
                    ...partial,
                },
            },
            createIfAbsent: false,
        }));
    };

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        field: keyof AlertSettingsType
    ) => {
        updateSettings({ [field]: event.target.checked });
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                CHART LINE VISIBILITY
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!showAlertLines}
                                onChange={(e) => handleCheckboxChange(e, "showAlertLines")}
                            />
                        }
                        label="Alert lines"
                        sx={checkboxColorLabelSx}
                    />
                    <ColorSelector
                        compact
                        showOnlySwatch
                        color={alertLineColor}
                        opacity={alertLineColorOpacity}
                        onColorChange={(color, opacity) =>
                            updateSettings({ alertLineColor: color, alertLineColorOpacity: opacity })
                        }
                    />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!onlyActiveAlerts}
                                onChange={(e) => handleCheckboxChange(e, "onlyActiveAlerts")}
                            />
                        }
                        label="Only active alerts"
                        sx={{ ...checkboxLabelSx, whiteSpace: 'nowrap' }}
                    />
                </Box>
            </FormGroup>
        </Box>
    );
};

export default AlertSettings;
